import { Component, type MouseEvent, type ReactNode } from 'react';
import { type RelayProp, graphql, createFragmentContainer } from 'react-relay';
import { connect, type DispatchProp } from 'react-redux';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';

import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight.ts';
import makeOfferLauncher from 'dibs-buyer-layout/exports/makeOfferLauncher.jsx';
// mutations
import { SharedModifyPortfolioItems } from '../../mutations/FindingSharedModifyPortfolioItems.ts';

// styles
import styles from './PdpSharedButtons.scss';
import classnames from 'classnames';

// helpers
import { redirectToOMT } from './utils.ts';
import { type Maybe } from 'dibs-ts-utils/exports/Maybe.ts';
import {
    trackMakeOfferClick,
    trackMakeOfferFromEmail,
    locations,
    type Location,
} from '../tracking/actionButtonsTracking.ts';
import { setCartItems } from '../../actions/sharedMobileActions.js';
import { updateMakeOfferModalShown } from '../../actions/pdpRespMakeOfferModalActions.js';
import { getQueryParam } from '../../utils/uriUtils.ts';
import { isMultiItemCheckoutVariant } from 'dibs-checkout/exports/useMultiItemCheckout.ts';

import { type PdpSharedMakeOfferButton_item$data } from './__generated__/PdpSharedMakeOfferButton_item.graphql.ts';
import { type PdpSharedMakeOfferButton_user$data } from './__generated__/PdpSharedMakeOfferButton_user.graphql.ts';

type DefaultProps = {
    size: 'small' | 'medium' | 'large';
    showArrow: boolean;
    hasPrivateOffer?: Maybe<boolean>;
    shouldLaunchContactDealer?: boolean;
};

type Props = {
    userId?: string | null;
    itemId: string;
    selectedSkuId?: string;
    item: PdpSharedMakeOfferButton_item$data;
    currency: string;
    relay: RelayProp;
    user: PdpSharedMakeOfferButton_user$data | null | undefined;
    quantitySelected?: string;
    gaLabel?: string;
    location?: Location;
    className?: string;
} & DefaultProps &
    DispatchProp;

type State = {
    isModalOpen: boolean;
};

let makeOfferModalShown = false;

class PdpSharedMakeOfferButtonComponentClass extends Component<Props, State> {
    static defaultProps: DefaultProps = {
        size: 'large',
        showArrow: true,
        hasPrivateOffer: false,
        shouldLaunchContactDealer: true,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            isModalOpen: false,
        };

        this.onClick = this.onClick.bind(this);
    }

    componentDidMount(): void {
        const queryParams = global?.location?.search;
        const modalParam = getQueryParam(queryParams, 'modal');
        const templateParam = getQueryParam(queryParams, 'utm_campaign');
        /**
         * This component might be used in multiple places on the PDP, as well as be mounted more
         * than once. Use `makeOfferModalShown` to avoid showing more than one modal at a time.
         */
        if (modalParam === 'offer' && !makeOfferModalShown) {
            if (!this.state.isModalOpen) {
                this.onClick();
            }

            if (templateParam) {
                trackMakeOfferFromEmail(templateParam, {
                    pdpTrackingVpvParams: this.props.item.makeOfferVpvParams,
                });
            }
        }
    }

    onClick(event?: MouseEvent): void {
        const {
            dispatch,
            userId,
            itemId,
            selectedSkuId,
            relay,
            quantitySelected,
            currency,
            hasPrivateOffer,
            shouldLaunchContactDealer,
            gaLabel,
            location,
        } = this.props;
        const { makeOfferVpvParams: pdpTrackingVpvParams, ...item } = this.props.item;
        const isMultiItemCheckout = isMultiItemCheckoutVariant();

        trackMakeOfferClick({
            item: { ...item, pdpTrackingVpvParams },
            hasPrivateOffer,
            event,
            location,
            environment: relay.environment,
            quantity: Number(quantitySelected),
        });
        if (!isMultiItemCheckout) {
            if (!userId) {
                // If no user, store item - it can be ADDed to cart when user logs in later
                dispatch(setCartItems(itemId));
            } else {
                // ADD item to user's cart
                SharedModifyPortfolioItems.commit(relay.environment, {
                    action: 'ADD',
                    itemId,
                    portfolioType: 'CART',
                    userId,
                });
            }
        }

        const { serviceId: transactionId, hasCompletedCheckout } =
            item?.lastActiveTransaction || {};

        if (hasPrivateOffer && transactionId && hasCompletedCheckout) {
            redirectToOMT(transactionId);
        } else {
            this.setState(
                {
                    isModalOpen: true,
                },
                () => {
                    makeOfferModalShown = true;
                    makeOfferLauncher({
                        itemId,
                        selectedSkuId,
                        currency,
                        environment: relay.environment,
                        quantitySelected,
                        shouldLaunchContactDealer,
                        launchOrigin: 'pdp-shared',
                        gaLabel,
                        page: 'pdp',
                        onClose: () => dispatch(updateMakeOfferModalShown(false)),
                        onOpen: () => dispatch(updateMakeOfferModalShown(true)),
                    });
                }
            );
        }
    }

    render(): ReactNode {
        const { size, showArrow, item, hasPrivateOffer, location, className } = this.props;
        const isPrivateVisibilityType = item?.isPrivateVisibilityType;
        const offerButton =
            !isPrivateVisibilityType && location !== locations.SIMILAR_SOLD ? (
                <FormattedMessage
                    id="PdpSharedMakeOfferButton.suggestPriceCta"
                    defaultMessage="Suggest A Price"
                />
            ) : (
                <FormattedMessage
                    id="PdpSharedMakeOfferButton.makeOfferCta"
                    defaultMessage="Make An Offer"
                />
            );

        return (
            <Button
                type={isPrivateVisibilityType ? 'secondaryAlt' : 'secondary'}
                className={classnames(styles.button, className)}
                dataTn="item-actions-make-offer"
                onClick={this.onClick}
                size={size}
                fullWidth
            >
                <div className={styles.makeOfferButton}>
                    {hasPrivateOffer ? (
                        <FormattedMessage
                            id="PdpSharedMakeOfferButton.counterOffer"
                            defaultMessage="Counter Offer"
                        />
                    ) : (
                        offerButton
                    )}
                    {showArrow && size !== 'small' && <ArrowRight className={styles.arrowSVG} />}
                </div>
            </Button>
        );
    }
}

export const PdpSharedMakeOfferButtonComponent = connect()(PdpSharedMakeOfferButtonComponentClass);

export const PdpSharedMakeOfferButton = createFragmentContainer(PdpSharedMakeOfferButtonComponent, {
    item: graphql`
        fragment PdpSharedMakeOfferButton_item on Item {
            ...SharedContactDealerWrapper_item
            ...actionButtonsTracking_item
            makeOfferVpvParams: pdpTrackingVpvParams(
                userId: $userId
                excludedPurchaseStatuses: [RET, CUST]
            ) @include(if: $afterDisplay)
            isPrivateVisibilityType
            lastActiveTransaction(buyerId: $userId) {
                serviceId
                hasCompletedCheckout
            }
        }
    `,
    user: graphql`
        fragment PdpSharedMakeOfferButton_user on User {
            ...SharedContactDealerWrapper_user
        }
    `,
});
